/**
 * Registar ekrana — stabilan identitet svakog ekrana i dijaloga, onako kako ga vidi asistent (AB#7590).
 *
 * ⚠️⚠️ Zašto registar, a ne ono što aplikacija već ima:
 *   - RUTA je implementacija. Reorganizacija menija ili preimenovanje slug-a tiho bi razbili svako mapiranje
 *     ekran → dokumentacija, a da nijedan test to ne primeti.
 *   - `data.breadcrumb` je srpski tekst, nelokalizovan, i nije jedinstven čim ekran dobije dijalog koji
 *     nosi svoj posao (dijalog nema ni rutu ni breadcrumb, a asistentu je to ipak drugo mesto).
 *
 * Zato: eksplicitan registar, sa id-jem koji je ASCII, nikad preveden i nikad izveden iz URL-a. Id je
 * HIJERARHIJSKI (`protax.properties.list`, `protax.properties.import`), da bi se kasnije mogao dodati finiji
 * nivo bez preimenovanja postojećeg — isti dogovor kao u MHI-jevom registru.
 *
 * ⚠️ Ime ekrana NIJE u registru. Registar nosi `nameKey` (`screens.<id>`), a tekst se razrešava kroz
 * `CopyService.t(screen.nameKey)` — tako ostaje lokalizovan (sr/en) i jedinstven po id-ju, a registar se ne
 * menja kad se menja tekst. Ključ stoji ovde kao DOSLOVAN string, ne sastavljen iz id-ja: `check:copy` nalazi
 * ključ samo kao literal, a sastavljen bi prijavio kao neupotrebljen.
 *
 * Za asistenta ga vezuje `assistant-screen.ts` (`provideEgAssistantScreen` u `app.config.ts`): id, ime iz
 * kopije i putanja idu serveru kao `uiContext`. Registar ostaje izvor istine, čuvan testom protiv prave tabele
 * ruta (`screen-registry.spec.ts`).
 */
export type ScreenKind = 'list' | 'detail' | 'form' | 'dashboard' | 'admin' | 'system';

export interface ScreenDescriptor {
    /** Stabilan, ASCII, hijerarhijski. Nikad se ne prevodi. Oko ovoga se dogovaraju asistent i korpus. */
    readonly id: string;
    /** Ključ kopije za ime ekrana — uvek `screens.<id>`; tekst razrešava `CopyService`. */
    readonly nameKey: string;
    readonly kind: ScreenKind;
    /**
     * Ovlašćenje koje ekran traži; bez njega ga vidi svaki prijavljeni korisnik. Jedino koje danas postoji je
     * super-admin (`requireSuperAdmin` na ruti, `Protax:SuperAdmin` politika na serveru) — test drži da se
     * registar i guard na ruti slažu.
     */
    readonly permission?: string;
}

/** Ekran do kog vodi ruta. `path` je apsolutni šablon kao u ruteru, npr. `/aplikacija/kontrolna-tabla`. */
export interface RoutedScreen extends ScreenDescriptor {
    readonly path: string;
}

/**
 * Dijalog koji nosi svoj posao, pa i svoj id. Nema rutu — otvara se nad ekranom `host`, a id mu stoji u
 * imenskom prostoru tog ekrana (`protax.properties.list` → `protax.properties.import`).
 */
export interface DialogScreen extends ScreenDescriptor {
    readonly kind: 'form';
    readonly host: string;
    /**
     * Fajl sa `<p-dialog>`-om, relativno na `src/app`. Spisak dijaloga se vodi RUKOM (dijalog nema rutu koju
     * bi test prošetao), pa `npm run check:copy` drži vezu: svaki `<p-dialog>` u kodu mora imati zapis ovde.
     */
    readonly source: string;
}

/** Oznaka super-admin ovlašćenja — ime serverske politike, ne tekst za korisnika. */
export const SUPER_ADMIN = 'Protax:SuperAdmin';

/**
 * Asistentska traka stoji u korenu aplikacije i živi i na 404, pa razrešavač MORA imati gde da padne.
 * Nepoznat ekran nikad nije izuzetak.
 */
export const UNKNOWN_SCREEN: ScreenDescriptor = { id: 'protax.unknown', nameKey: 'screens.protax.unknown', kind: 'system' };

/**
 * Redosled deklaracije JE redosled poklapanja, kao u ruteru: doslovan segment mora stajati pre `:param`
 * šablona koji bi ga takođe progutao. Danas nijedna ruta nema parametar, ali algoritam je isti kao u MHI-ju,
 * da prvi takav ekran ne bi tražio novi razrešavač.
 *
 * Preusmerenja (`/aplikacija` → kontrolna tabla, `/aplikacija/administracija` → pravna lica) namerno nisu
 * ovde: ruter posle preusmerenja javlja ciljnu adresu, pa izvorna nikad ne stigne do razrešavača.
 */
export const ROUTED_SCREENS: readonly RoutedScreen[] = [
    // ── Van ljuske: traka stoji u korenu aplikacije, pa se vidi i ovde čim je korisnik prijavljen ──────
    { path: '/', id: 'protax.public.landing', nameKey: 'screens.protax.public.landing', kind: 'system' },
    { path: '/zabranjen-pristup', id: 'protax.access-denied', nameKey: 'screens.protax.access-denied', kind: 'system' },

    // ── Aplikacija ──────────────────────────────────────────────────────────────────────────────────────
    { path: '/aplikacija/kontrolna-tabla', id: 'protax.dashboard', nameKey: 'screens.protax.dashboard', kind: 'dashboard' },
    { path: '/aplikacija/pregled-nepokretnosti', id: 'protax.properties.list', nameKey: 'screens.protax.properties.list', kind: 'list' },
    { path: '/aplikacija/evidencija-prijava', id: 'protax.tax-returns.list', nameKey: 'screens.protax.tax-returns.list', kind: 'list' },
    { path: '/aplikacija/informacije-o-zonama', id: 'protax.zones', nameKey: 'screens.protax.zones', kind: 'list' },
    { path: '/aplikacija/administracija/pravna-lica', id: 'protax.admin.entities', nameKey: 'screens.protax.admin.entities', kind: 'admin', permission: SUPER_ADMIN },
    // Podrška (Ticketing) — ekran biblioteke @expertgroup/support. `system`: nije poreski ekran, pa asistent tu ne
    // nudi pitanja o porezu na imovinu.
    { path: '/aplikacija/podrska', id: 'protax.support', nameKey: 'screens.protax.support', kind: 'system' },
    // Kanarinac za uski token (AB#6533) — nije u meniju, ali ruta postoji i traka se na njoj vidi.
    { path: '/aplikacija/asistent-proba', id: 'protax.assistant.probe', nameKey: 'screens.protax.assistant.probe', kind: 'system' }
];

export const DIALOG_SCREENS: readonly DialogScreen[] = [
    // Isti dijalog za novu i za izmenu postojeće nepokretnosti — jedan posao, dva ulaza.
    { id: 'protax.properties.form', nameKey: 'screens.protax.properties.form', kind: 'form', host: 'protax.properties.list', source: 'features/properties/property-form-dialog.ts' },
    { id: 'protax.properties.import', nameKey: 'screens.protax.properties.import', kind: 'form', host: 'protax.properties.list', source: 'features/properties/import-properties-dialog.ts' },
    { id: 'protax.tax-returns.create', nameKey: 'screens.protax.tax-returns.create', kind: 'form', host: 'protax.tax-returns.list', source: 'features/tax-returns/create-return-dialog.ts' },
    // Evidentiranje i izmena detalja podnošenja — isti dijalog, dva režima.
    { id: 'protax.tax-returns.submit', nameKey: 'screens.protax.tax-returns.submit', kind: 'form', host: 'protax.tax-returns.list', source: 'features/tax-returns/submission-dialog.ts' },
    { id: 'protax.tax-returns.amend-base', nameKey: 'screens.protax.tax-returns.amend-base', kind: 'form', host: 'protax.tax-returns.list', source: 'features/tax-returns/amend-base-dialog.ts' },
    { id: 'protax.admin.entities.plan', nameKey: 'screens.protax.admin.entities.plan', kind: 'form', host: 'protax.admin.entities', source: 'features/admin/business-entities.ts', permission: SUPER_ADMIN }
];

/** Svi ekrani i dijalozi — rutirani redom poklapanja, pa dijalozi. */
export const SCREENS: readonly ScreenDescriptor[] = [...ROUTED_SCREENS, ...DIALOG_SCREENS];

const COMPILED = ROUTED_SCREENS.map((entry) => ({ entry, pattern: toPattern(entry.path) }));

/**
 * Razrešava URL rutera u deskriptor. Query i fragment se skidaju (nose stanje tabele, nikad identitet),
 * završna kosa crta se toleriše, prvi pogodak po redosledu deklaracije pobeđuje. Sve ostalo je
 * {@link UNKNOWN_SCREEN}.
 */
export function resolveScreen(url: string): ScreenDescriptor {
    const path = normalize(url);
    const hit = COMPILED.find(({ pattern }) => pattern.test(path));
    return hit ? descriptorOf(hit.entry) : UNKNOWN_SCREEN;
}

/** Deskriptor po id-ju — za dijaloge, koji nemaju URL po kom bi se razrešili. */
export function screenById(id: string): ScreenDescriptor | undefined {
    const entry = SCREENS.find((s) => s.id === id);
    return entry ? descriptorOf(entry) : undefined;
}

/** Samo polja deskriptora — `path`, `host` i `source` su unutrašnja stvar registra, ne kontekst ekrana. */
function descriptorOf(entry: ScreenDescriptor): ScreenDescriptor {
    const { id, nameKey, kind, permission } = entry;
    return permission === undefined ? { id, nameKey, kind } : { id, nameKey, kind, permission };
}

function normalize(url: string): string {
    const withoutQuery = url.split(/[?#]/)[0];
    const trimmed = withoutQuery.length > 1 ? withoutQuery.replace(/\/+$/, '') : withoutQuery;
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

function toPattern(path: string): RegExp {
    const source = path
        .split('/')
        .map((segment) => (segment.startsWith(':') ? '[^/]+' : segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
        .join('/');
    return new RegExp(`^${source}$`);
}
